/**
 * Vietnamese-locale formatting helpers. All UI display values pass through
 * one of these so the entire app speaks the same dialect of dates / numbers.
 */

import type {
  AllergySeverity,
  AlertSeverity,
  ConditionSeverity,
  GuardianRelationship,
  PatientGender,
  PatientStatus,
  AllergyCategory,
  AlertType,
  BloodType,
  DuplicateConfidence,
} from "./types/patient";
import type { AppointmentStatus } from "./types/appointment";
import type {
  MealTiming,
  MedicineClassification,
  VatCategory,
} from "./types/medicine";
import type { InviteStatus, StaffRole, StaffStatus } from "./types/staff";
import type {
  DiscountType,
  InvoiceStatus,
  PaymentMethod,
} from "./types/billing";

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatPhone(value: string | null | undefined): string {
  if (!value) return "—";
  // E.164 "+84967369125" → "0967 369 125" for display  // phi-scan-ok: docstring example
  const m = value.match(/^\+84(\d{2,3})(\d{3})(\d{3,4})$/);
  if (m) return `0${m[1]} ${m[2]} ${m[3]}`;
  return value;
}

export const genderLabel: Record<PatientGender, string> = {
  M: "Nam",
  F: "Nữ",
  OTHER: "Khác",
  UNKNOWN: "Không rõ",
};

export const statusLabel: Record<PatientStatus, string> = {
  ACTIVE: "Đang điều trị",
  ARCHIVED: "Đã lưu trữ",
  MERGED: "Đã gộp",
  DECEASED: "Đã qua đời",
};

export const allergyCategoryLabel: Record<AllergyCategory, string> = {
  DRUG: "Thuốc",
  FOOD: "Thực phẩm",
  ENVIRONMENT: "Môi trường",
  OTHER: "Khác",
};

export const allergySeverityLabel: Record<AllergySeverity, string> = {
  MILD: "Nhẹ",
  MODERATE: "Vừa",
  SEVERE: "Nặng",
  ANAPHYLAXIS: "Sốc phản vệ",
};

export const conditionSeverityLabel: Record<ConditionSeverity, string> = {
  MILD: "Nhẹ",
  MODERATE: "Vừa",
  SEVERE: "Nặng",
};

export const alertSeverityLabel: Record<AlertSeverity, string> = {
  INFO: "Thông tin",
  WARNING: "Cảnh báo",
  CRITICAL: "Khẩn",
};

export const alertTypeLabel: Record<AlertType, string> = {
  ALLERGY: "Dị ứng",
  DRUG_INTERACTION: "Tương tác thuốc",
  PREGNANCY: "Thai kỳ",
  HIGH_RISK: "Nguy cơ cao",
  OVERDUE_FOLLOWUP: "Quá hạn tái khám",
  CUSTOM: "Khác",
};

export const guardianRelationshipLabel: Record<GuardianRelationship, string> = {
  MOTHER: "Mẹ",
  FATHER: "Bố",
  SPOUSE: "Vợ/chồng",
  CHILD: "Con",
  SIBLING: "Anh/chị/em",
  OTHER: "Khác",
};

export const bloodTypeLabel: Record<BloodType, string> = {
  A_POS: "A+",
  A_NEG: "A−",
  B_POS: "B+",
  B_NEG: "B−",
  AB_POS: "AB+",
  AB_NEG: "AB−",
  O_POS: "O+",
  O_NEG: "O−",
  UNKNOWN: "Không rõ",
};

export const duplicateConfidenceLabel: Record<DuplicateConfidence, string> = {
  STRONG: "Trùng chắc chắn",
  LIKELY: "Có khả năng trùng",
  WEAK: "Có thể trùng",
};

export const appointmentStatusLabel: Record<AppointmentStatus, string> = {
  REQUESTED: "Chờ xác nhận",
  CONFIRMED: "Đã xác nhận",
  CHECKED_IN: "Đã nhận khám",
  COMPLETED: "Đã khám xong",
  NO_SHOW: "Không đến",
  CANCELLED: "Đã hủy",
};

export const medicineClassificationLabel: Record<MedicineClassification, string> = {
  DRUG: "Thuốc",
  SUPPLEMENT: "Thực phẩm chức năng",
  SUPPLY: "Vật tư",
  VACCINE: "Vắc xin",
  OTHER: "Khác",
};

export const mealTimingLabel: Record<MealTiming, string> = {
  BEFORE_MEAL: "Trước bữa ăn",
  WITH_MEAL: "Trong bữa ăn",
  AFTER_MEAL: "Sau bữa ăn",
  EMPTY_STOMACH: "Lúc đói",
  BEDTIME: "Trước khi ngủ",
  ANYTIME: "Bất kỳ lúc nào",
};

export const vatCategoryLabel: Record<VatCategory, string> = {
  KCT: "Không chịu thuế (KCT)",
  VAT_0: "Thuế suất 0%",
  VAT_5: "Thuế suất 5%",
  VAT_8: "Thuế suất 8%",
  VAT_10: "Thuế suất 10%",
};

export function formatVnd(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("vi-VN").format(value);
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

const WEEKDAYS_SHORT = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
export function formatWeekday(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return WEEKDAYS_SHORT[d.getDay()] ?? "";
}

export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Ngày nghiệp vụ `YYYY-MM-DD` lệch `days` ngày so với `from`, theo GIỜ ĐỊA PHƯƠNG (#19847).
 *
 * Không dùng `toISOString().slice(0, 10)` cho ngày nghiệp vụ: nó trả ngày theo UTC, nên ở +07 từ
 * 00:00 đến 06:59 sáng nó trả HÔM QUA.
 */
export function shiftIsoDate(from: Date, days: number): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
  return isoDate(d);
}

/** Khoảng `[hôm nay − back, hôm nay]` theo giờ địa phương — mặc định của các trang báo cáo. */
export function recentDaysRange(back: number, now: Date = new Date()): { from: string; to: string } {
  return { from: shiftIsoDate(now, -back), to: isoDate(now) };
}

/** 12 tháng gần nhất tính cả tháng này: từ ngày 1 của tháng cách đây 11 tháng tới hôm nay. */
export function recentMonthsRange(now: Date = new Date()): { from: string; to: string } {
  return { from: isoDate(new Date(now.getFullYear(), now.getMonth() - 11, 1)), to: isoDate(now) };
}

/**
 * Mọi ngày `YYYY-MM-DD` từ `from` tới `to` (cả hai đầu). Dùng để ĐIỀN 0 cho ngày không có dòng:
 * báo cáo ngày chỉ trả những ngày có dữ liệu, nên chỉ số mảng không phải là ngày.
 */
export function dateRangeDays(from: string, to: string): string[] {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  const out: string[] = [];
  const end = new Date(ty, tm - 1, td);
  for (let d = new Date(fy, fm - 1, fd); d <= end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    out.push(isoDate(d));
  }
  return out;
}

export function startOfWeek(d: Date): Date {
  const out = new Date(d);
  const day = out.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // anchor to Monday
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
}

export const staffRoleLabel: Record<StaffRole, string> = {
  DOCTOR: "Bác sĩ",
  NURSE: "Điều dưỡng",
  RECEPTIONIST: "Lễ tân",
  MANAGER: "Quản lý",
  OWNER: "Chủ phòng khám",
  OTHER: "Khác",
};

export const staffStatusLabel: Record<StaffStatus, string> = {
  ACTIVE: "Đang làm",
  LEAVE: "Tạm nghỉ",
  TERMINATED: "Đã nghỉ việc",
};

export const inviteStatusLabel: Record<InviteStatus, string> = {
  SENT: "Đã gửi",
  ACCEPTED: "Đã kích hoạt",
  REVOKED: "Đã hủy",
  EXPIRED: "Hết hạn",
};

/**
 * RBAC role → display label (permission-focused, distinct from HR staffRoleLabel).
 * These map to the `code` field returned by /api/v1/roles.
 */
export const systemRoleLabel: Record<string, string> = {
  clinic_owner:  "Quản trị toàn hệ thống",
  doctor:        "Kê đơn & Khám bệnh",
  receptionist:  "Tiếp đón & Thu ngân",
  manager:       "Quản lý vận hành",
  nurse:         "Hỗ trợ lâm sàng",
};

/**
 * Short description for each RBAC role — shown in role-picker chips and
 * the Roles settings page to help staff understand what each role grants.
 */
export const systemRoleDescription: Record<string, string> = {
  clinic_owner:  "Toàn quyền: cài đặt phòng khám, quản lý nhân sự, xem báo cáo tài chính.",
  doctor:        "Khám bệnh, lập hồ sơ lâm sàng, kê đơn thuốc, ký hồ sơ.",
  receptionist:  "Tiếp nhận lịch hẹn, quản lý hàng chờ, thu ngân, in hoá đơn.",
  manager:       "Xem toàn bộ báo cáo, quản lý lịch làm việc, phê duyệt nghỉ phép.",
  nurse:         "Hỗ trợ quy trình khám, cập nhật dấu hiệu sinh tồn, chuẩn bị thuốc.",
};

export const invoiceStatusLabel: Record<InvoiceStatus, string> = {
  DRAFT: "Nháp",
  POSTED: "Đã ghi nhận",
  PARTIALLY_PAID: "Thu một phần",
  PAID: "Đã thu đủ",
  VOIDED: "Đã hủy",
  REFUNDED: "Đã hoàn tiền",
};

export const paymentMethodLabel: Record<PaymentMethod, string> = {
  CASH: "Tiền mặt",
  CARD: "Thẻ",
  BANK_TRANSFER: "Chuyển khoản",
  VNPAY: "VNPAY",
  MOMO: "Momo",
};

export const discountTypeLabel: Record<DiscountType, string> = {
  PERCENT: "%",
  AMOUNT: "đ",
};

/** Format VND with the đ suffix — used in headings + totals lines. */
export function formatVndWithUnit(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${new Intl.NumberFormat("vi-VN").format(value)} ₫`;
}

/** Days until a date in the future. Returns null for null inputs, negative for past dates. */
export function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.floor((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}


export function initials(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const last = words[words.length - 1] ?? "";
  if (words.length === 1) return last.slice(0, 2).toUpperCase();
  const first = words[0] ?? "";
  return (first[0]! + last[0]!).toUpperCase();
}
